import { memo, useEffect, useRef } from "react";

import { setSelectedLine } from "../model/actions";
import { areLineNameEqual } from "../utils";
import useAppDispatch from "../utils/hooks/useAppDispatch";
import useAppSelector from "../utils/hooks/useAppSelector";

/**
 * Selects the line matching the "line" query parameter whenever the lines
 * list changes. Extracted from LiveMap's onLinesChange.
 */
function LinesChangeListener() {
  const dispatch = useAppDispatch();
  const config = useAppSelector((state) => state.config);
  const lineQueryParam = useAppSelector((state) => state.line);
  const lines = useAppSelector((state) => state.lines);
  // onLinesChange was only called from componentDidUpdate, never on mount.
  const isMountedRef = useRef(false);
  // Read via refs so the effect only needs to depend on lines, the only
  // value that triggers onLinesChange in the original class.
  const configRef = useRef(config);
  const lineQueryParamRef = useRef(lineQueryParam);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    lineQueryParamRef.current = lineQueryParam;
  }, [lineQueryParam]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    if (!configRef.current.lineParam || !lineQueryParamRef.current) {
      return;
    }

    const line = lines.find(
      ({ name }) => !!areLineNameEqual(name, lineQueryParamRef.current),
    );

    if (!line) {
      return;
    }

    dispatch(setSelectedLine(line));
  }, [dispatch, lines]);

  return null;
}

export default memo(LinesChangeListener);
